/**
 * Recording filters for algolia search
 *
 * To be used inside a algolia search context (set by InstantSearch component)
 */
import * as React from 'react';
import { useInstantSearch, useRange, useRefinementList } from 'react-instantsearch-hooks-web';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import ReactSlider from 'react-slider';
import MoodFilterIcon from './images/mood-filter-icon.inline.svg';
import HabitatFilterIcon from './images/habitat-filter-icon.inline.svg';
import RecordistFilterIcon from './images/recordist-filter-icon.inline.svg';
import PredominantSoundFilterIcon from './images/predominant-sound-filter-icon.inline.svg';
import Select from '../Select';
import { PlainButton } from '../styled-components';
import OptionPickerModal from '../OptionPickerModal';
import { secondsToHhMmSs } from '../../utils/common';

const durationOptions = [
  {
    value: '0-10',
    label: 'under 10 min',
  },
  {
    value: '10-20',
    label: '10 to 20 min',
  },
  {
    value: '20-30',
    label: '20 to 30 min',
  },
  {
    value: '30-60',
    label: '30 to 60 min',
  },
  {
    value: '60-80',
    label: '60 to 80 min',
  },
  {
    value: '80-10000',
    label: '80+ min',
  },
];

function refinementItemsToOptions(items: any[]) {
  return items.map((item) => ({
    value: item.value,
    label: item.label,
  }));
}

const Wrapper = styled.div`
  --recording-filter-gap: 12px;
  position: relative;
  display: flex;
  margin-right: calc(-1 * var(--earth-fm--container--padding));
  padding-bottom: 20px;
  overflow-x: auto;
  z-index: 3;

  // space at the end of the filters when the user scrolls
  &::after {
    content: '';
    width: var(--recording-filter-gap);
    height: 1px;
    flex-shrink: 0;
  }

  .soundmap-filter {
    width: 100%;

    &:not(:first-of-type) {
      margin-left: var(--recording-filter-gap);
    }
  }

  @media (min-width: 576px) {
    margin-right: calc(
      -1 * (
          100vw + 2 * var(--earth-fm--container--padding) - var(--earth-fm--container--max-width)
        ) / 2
    );
  }

  @media (min-width: 992px) {
    margin-right: 0;

    &::after {
      content: none;
    }
  }

  @media (min-width: 1400px) {
    --recording-filter-gap: 34px;
  }
`;

const MobileWrapper = styled.div`
  margin-top: 20px;
`;

const FilterButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  overflow-x: auto;

  button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6.5px;
    white-space: nowrap;
  }
`;

const DurationWrapper = styled.div`
  margin: 20px calc(-1 * var(--earth-fm--container--padding));
  padding: 0 var(--earth-fm--container--padding);
  padding-top: 20px;
  border-top: 1px solid var(--earth-fm--color--green-15);
`;

const DurationTitle = styled.div`
  font-weight: 600;
  margin-bottom: 20px;
`;

const DurationLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const DurationSlider = styled(ReactSlider)`
  width: 100%;
  height: 12px;
  margin-bottom: 5px;

  .duration-slider {
    &__track {
      height: 4px;
      background-color: var(--earth-fm--color--green-15);
      border-radius: 2px;

      &-1 {
        background-color: var(--earth-fm--color--green);
      }
    }

    &__thumb {
      width: 12px;
      height: 12px;
      margin-top: 2px;
      background-color: var(--earth-fm--color--green);
      border-radius: 50%;
      transform: translateY(-50%);

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 3em;
        height: 3em;
        transform: translate(-50%, -50%);
      }
    }
  }
`;

const filterLabels = {
  'meta.mood': 'Mood',
  'meta.predominantSound': 'Predominant sound',
  'meta.habitat': 'Ecosystem',
  'meta.recordist': 'Recordist',
};

const maxPossibleDuration = 3 * 60 * 60; // 3 hours in seconds

export default function SearchFilters() {
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const [activeMobileFilter, setActiveMobileFilter] = React.useState<
    'meta.mood' | 'meta.predominantSound' | 'meta.habitat' | 'meta.recordist' | null
  >(null);

  const durationRange = useRange({
    attribute: 'meta.duration',
    min: 0,
    max: maxPossibleDuration,
  });

  const moodRefinement = useRefinementList({
    attribute: 'meta.mood',
    sortBy: ['count', 'name'],
  });

  const predominantSoundRefinement = useRefinementList({
    attribute: 'meta.predominantSound',
    sortBy: ['count', 'name'],
  });

  const habitatRefinement = useRefinementList({
    attribute: 'meta.habitat',
    sortBy: ['count', 'name'],
  });

  const recordistRefinement = useRefinementList({
    attribute: 'meta.recordist',
    sortBy: ['count', 'name'],
  });

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const activeMobileFilterOptions = React.useMemo(() => {
    if (activeMobileFilter === 'meta.mood') {
      return refinementItemsToOptions(moodRefinement.items);
    }
    if (activeMobileFilter === 'meta.predominantSound') {
      return refinementItemsToOptions(predominantSoundRefinement.items);
    }
    if (activeMobileFilter === 'meta.habitat') {
      return refinementItemsToOptions(habitatRefinement.items);
    }
    if (activeMobileFilter === 'meta.recordist') {
      return refinementItemsToOptions(recordistRefinement.items);
    }
    return [];
  }, [
    activeMobileFilter,
    moodRefinement.items,
    predominantSoundRefinement.items,
    habitatRefinement.items,
    recordistRefinement.items,
  ]);

  const handleFilterChange = React.useCallback(
    (attribute: string, selected: any[]) => {
      setIndexUiState({
        ...indexUiState,
        refinementList: {
          ...indexUiState.refinementList,
          [attribute]: selected,
        },
      });
    },
    [indexUiState, setIndexUiState],
  );

  const handleDurationChange = React.useCallback(
    (selected: string[]) => {
      if (!selected.length) {
        durationRange.refine([0, maxPossibleDuration]);
        return;
      }

      const values = selected.map((s) => s.split('-').map((v) => parseInt(v, 10) * 60)).flat();
      durationRange.refine([Math.min(...values), Math.max(...values)]);
    },
    [durationRange],
  );

  const selectedMoods = React.useMemo(
    () => indexUiState.refinementList?.['meta.mood'] || [],
    [indexUiState.refinementList],
  );

  const selectedDurations = React.useMemo(
    () => (durationRange.start ? [durationRange.start.join('-')] : []),
    [durationRange.start],
  );

  const selectedPredominantSounds = React.useMemo(
    () => indexUiState.refinementList?.['meta.predominantSound'] || [],
    [indexUiState.refinementList],
  );

  const selectedHabitats = React.useMemo(
    () => indexUiState.refinementList?.['meta.habitat'] || [],
    [indexUiState.refinementList],
  );

  const selectedRecordists = React.useMemo(
    () => indexUiState.refinementList?.['meta.recordist'] || [],
    [indexUiState.refinementList],
  );

  // It's safe to use useMediaQuery here because this component is only rendered after search is
  // opened by the user. Using useMediaQuery for conditionally rendering UI is not safe in SSR.
  if (isMobile) {
    return (
      <MobileWrapper>
        <FilterButtons>
          <PlainButton onClick={() => setActiveMobileFilter('meta.mood')}>
            <MoodFilterIcon />
            <span>Mood {selectedMoods.length > 0 && `(${selectedMoods.length})`}</span>
          </PlainButton>
          <PlainButton onClick={() => setActiveMobileFilter('meta.predominantSound')}>
            <PredominantSoundFilterIcon />
            <span>
              Sound{' '}
              {selectedPredominantSounds.length > 0 && `(${selectedPredominantSounds.length})`}
            </span>
          </PlainButton>
          <PlainButton onClick={() => setActiveMobileFilter('meta.habitat')}>
            <HabitatFilterIcon />
            <span>Ecosystem {selectedHabitats.length > 0 && `(${selectedHabitats.length})`}</span>
          </PlainButton>
          <PlainButton onClick={() => setActiveMobileFilter('meta.recordist')}>
            <RecordistFilterIcon />
            <span>
              Recordist {selectedRecordists.length > 0 && `(${selectedRecordists.length})`}
            </span>
          </PlainButton>
        </FilterButtons>
        <DurationWrapper>
          <DurationTitle>Duration</DurationTitle>
          <DurationSlider
            thumbClassName="duration-slider__thumb"
            trackClassName="duration-slider__track"
            defaultValue={[durationRange.start[0] || 0, durationRange.start[1] || 0]}
            min={durationRange.range.min}
            max={durationRange.range.max}
            ariaLabel={['Lower thumb', 'Upper thumb']}
            ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
            renderThumb={(props, state) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <div {...props}>
                <span className="screen-reader-text">{secondsToHhMmSs(state.valueNow)}</span>
              </div>
            )}
            pearling
            minDistance={10}
            onAfterChange={(selected) =>
              selected instanceof Array && durationRange.refine([selected[0], selected[1]])
            }
          />
          <DurationLabels>
            <span>{secondsToHhMmSs(durationRange.start[0] || 0)}</span>
            <span>{secondsToHhMmSs(durationRange.start[1] || 0)}</span>
          </DurationLabels>
        </DurationWrapper>
        {activeMobileFilter && (
          <OptionPickerModal
            open={activeMobileFilter !== null}
            allowMultiple
            title={`Filter by ${filterLabels[activeMobileFilter].toLowerCase()}`}
            options={activeMobileFilterOptions}
            selected={indexUiState.refinementList?.[activeMobileFilter] || []}
            onSelect={(selected) => {
              handleFilterChange(activeMobileFilter, selected);
              setActiveMobileFilter(null);
            }}
            onClose={() => setActiveMobileFilter(null)}
          />
        )}
      </MobileWrapper>
    );
  }

  return (
    <Wrapper>
      <Select
        label="Mood"
        placeholder="Mood"
        className="soundmap-filter soundmap-filter__mood"
        isMulti
        labelHidden
        selected={selectedMoods}
        options={refinementItemsToOptions(moodRefinement.items)}
        onChange={(selected) => {
          handleFilterChange('meta.mood', selected);
        }}
      />
      <Select
        label="Duration"
        placeholder="Duration"
        className="soundmap-filter soundmap-filter__duration"
        labelHidden
        selected={selectedDurations}
        options={durationOptions}
        onChange={handleDurationChange}
      />
      <Select
        label="Predominant sound"
        placeholder="Predominant sound"
        className="soundmap-filter soundmap-filter__predominant-sound"
        isMulti
        labelHidden
        selected={selectedPredominantSounds}
        options={refinementItemsToOptions(predominantSoundRefinement.items)}
        onChange={(selected) => {
          handleFilterChange('meta.predominantSound', selected);
        }}
      />
      <Select
        label="Ecosystem"
        placeholder="Ecosystem"
        className="soundmap-filter soundmap-filter__habitat"
        isMulti
        labelHidden
        selected={selectedHabitats}
        options={refinementItemsToOptions(habitatRefinement.items)}
        onChange={(selected) => {
          handleFilterChange('meta.habitat', selected);
        }}
      />
      <Select
        label="Recordist"
        placeholder="Recordist"
        className="soundmap-filter soundmap-filter__recordist"
        isMulti
        labelHidden
        selected={selectedRecordists}
        options={refinementItemsToOptions(recordistRefinement.items)}
        onChange={(selected) => {
          handleFilterChange('meta.recordist', selected);
        }}
      />
    </Wrapper>
  );
}
